import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { OrderFormLayout } from "../OrderFormLayout";
import { OrderFormState } from "../state/OrderFormState";
import { createOrderBackendCall } from "./createOrderBackendCall";
import { OrderForm } from "../form/OrderForm";
import { API } from "aws-amplify";
import { Spinner } from "@goodfill/ui-components";
import { amplifyErrorsToString } from "../../../../amplifyErrorsToString";
import { UserAuthDataBalanceAccount } from "../../../backendApi/amplifyGraphQl/CustomGetUserData/UserData";
import { route } from "../../../../routes";
import { PricedBond } from "../../../bonds/PricedBond";
import { OrderFormResult, OrderReceipt } from "../receipt/OrderReceipt";
import { AccountId, OrderDirection, TradeTicketId } from "@goodfill/domain-properties";
import { useRefreshUserDataHook } from "../../../useRefreshUserDataHook";
import { doPortfolioCustomListPositions } from "../../../backendApi/amplifyGraphQl/PortfolioCustomListPositions/doPortfolioCustomListPositions";
import { PortfolioPositionTradeTicket } from "../../../backendApi/amplifyGraphQl/PortfolioCustomListPositions/PortfolioTradeTicket";
import { calculateGoodfillTransactionCommissionForAmount } from "@goodfill/domain-math";

export const CreateOrderContainer = ({
  orderDirection,
  pricedBond,
  tradeDate,
  accountId,
  moneyBalanceAccount,
  tradeTicketId,
}: {
  orderDirection: OrderDirection;
  pricedBond: PricedBond;
  tradeDate: DateTime;
  accountId: AccountId;
  moneyBalanceAccount: UserAuthDataBalanceAccount;
  tradeTicketId: TradeTicketId | null;
}) => {
  const navigate = useNavigate();

  const issuerName = pricedBond.issuerName;
  const moneyBalanceAccountId = moneyBalanceAccount.id;

  const refreshUser = useRefreshUserDataHook();

  const [tradeTicket, setTradeTicket] = useState<PortfolioPositionTradeTicket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [createdOrderResult, setCreatedOrderResult] = useState<OrderFormResult | null>(null);

  const breadcrumbItems = [
    {
      content: "GoodFill",
      onClick: () => {
        navigate(route.dashboard());
      },
    },
    {
      content: issuerName,
      onClick: () => {
        navigate(route.bondDetail(pricedBond.bondIdCusip));
      },
    },
    {
      content: orderDirection === OrderDirection.Sell ? "Sell Order" : "Buy Order",
    },
  ];

  const onSubmit = async (state: OrderFormState) => {
    setIsCreatingOrder(true);

    const rateCommission = calculateGoodfillTransactionCommissionForAmount(state.s.faceAmount);

    try {
      const createdOrderTicket = await createOrderBackendCall(
        state.i.pricedBond,
        state.getBasePrice(),
        moneyBalanceAccountId,
        state.s.faceAmount.amount,
        state.s.isAllOrNothing,
        state.s.customLoadedYield,
        rateCommission,
        state.isImmediate(),
        state.s.expirationDate,
        state.i.orderDirection,
        state.i.closingTradeTicketId,
      );

      await refreshUser();
      setCreatedOrderResult({ state, orderTicket: createdOrderTicket });
      toast("Request to create order submitted");
    } catch (e) {
      console.error(e);
      toast.error(amplifyErrorsToString(e));
    } finally {
      setIsCreatingOrder(false);
    }
  };

  useEffect(() => {
    if (tradeTicketId === null) {
      return;
    }

    let ignore = false;
    const init = async () => {
      if (ignore) {
        return;
      }
      setIsLoading(true);
      try {
        const result = await doPortfolioCustomListPositions(API, accountId);

        const tradeTicket = result.find((a) => a.id === tradeTicketId);

        if (ignore || tradeTicket === undefined) {
          return;
        }

        setTradeTicket(tradeTicket);
      } catch (e) {
        console.error(e);
        toast.error(amplifyErrorsToString(e));
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      ignore = true;
    };
  }, [tradeTicketId, accountId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (orderDirection === OrderDirection.Sell && tradeTicketId === null) {
    return <>Error: Sell must have TradeTicket to Sell</>;
  }

  const availableToSell =
    orderDirection === OrderDirection.Sell && tradeTicket?.availableToSell ? tradeTicket?.availableToSell : null;

  const defaultState = OrderFormState.createDefault(
    orderDirection,
    pricedBond,
    tradeDate,
    tradeTicket?.id ?? null,
    availableToSell,
  );

  return (
    <OrderFormLayout pricedBond={pricedBond} breadcrumbItems={breadcrumbItems}>
      {createdOrderResult !== null ? (
        <OrderReceipt
          result={createdOrderResult}
          moneyBalanceAccount={moneyBalanceAccount}
          showCalculatedInfo={true}
        />
      ) : (
        <OrderForm
          isLoading={isCreatingOrder}
          moneyBalanceAccount={moneyBalanceAccount}
          initState={defaultState}
          onSubmit={onSubmit}
          availableToSell={availableToSell}
        />
      )}
    </OrderFormLayout>
  );
};
